import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useCRM } from './CRMContext';
import crmApi from '../utils/crmApi';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const { token } = useCRM();
  const deliveredAnnouncementsRef = useRef(new Set());

  // Load notifications from localStorage on mount
  useEffect(() => {
    const savedNotifications = localStorage.getItem('crm-notifications');
    if (savedNotifications) {
      try {
        const parsed = JSON.parse(savedNotifications);
        setNotifications(parsed);
        updateUnreadCount(parsed);
      } catch (error) {
        console.error('Failed to parse saved notifications:', error);
      }
    }
  }, []);

  useEffect(() => {
    const delivered = localStorage.getItem('crm-announcements-delivered');
    if (delivered) {
      try {
        const parsed = JSON.parse(delivered);
        if (Array.isArray(parsed)) {
          deliveredAnnouncementsRef.current = new Set(parsed);
        }
      } catch (error) {
        console.error('Failed to parse delivered announcements list:', error);
      }
    }
  }, []);

  // Save notifications to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('crm-notifications', JSON.stringify(notifications));
    updateUnreadCount(notifications);
  }, [notifications]);

  const updateUnreadCount = useCallback((notificationsList) => {
    const unread = notificationsList.filter(n => !n.read).length;
    setUnreadCount(unread);
  }, []);

  const addNotification = useCallback((notification) => {
    const newNotification = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      sourceId: notification.sourceId || null,
      ...notification
    };

    console.log('[NotificationContext] Adding notification:', newNotification);

    setNotifications(prev => {
      if (newNotification.sourceId && prev.some(item => item.sourceId === newNotification.sourceId)) {
        return prev;
      }
      const updated = [newNotification, ...prev].slice(0, 100); // Keep only last 100 notifications
      console.log('[NotificationContext] Updated notifications count:', updated.length);
      return updated;
    });
  }, []);

  const markAsRead = useCallback((id) => {
    setNotifications(prev => 
      prev.map(notification => 
        notification.id === id 
          ? { ...notification, read: true }
          : notification
      )
    );
  }, []);

  const markAllAsRead = useCallback(() => {
    setNotifications(prev => 
      prev.map(notification => ({ ...notification, read: true }))
    );
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  }, []);

  const clearAllNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  const openNotificationCenter = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeNotificationCenter = useCallback(() => {
    setIsOpen(false);
  }, []);

  const toggleNotificationCenter = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  // Predefined notification creators
  const createNotification = {
    success: (title, message, options = {}) => addNotification({
      type: 'success',
      title,
      message,
      icon: 'CheckCircle',
      ...options
    }),

    error: (title, message, options = {}) => addNotification({
      type: 'error',
      title,
      message,
      icon: 'AlertCircle',
      ...options
    }),

    warning: (title, message, options = {}) => addNotification({
      type: 'warning',
      title,
      message,
      icon: 'AlertTriangle',
      ...options
    }),

    info: (title, message, options = {}) => {
      console.log('[createNotification.info] Called with:', { title, message, options });
      const notification = {
        type: 'info', // Default type
        title,
        message,
        icon: 'Info',
        ...options // Options override defaults (including type)
      };
      console.log('[createNotification.info] Calling addNotification with:', notification);
      addNotification(notification);
    },

    task: (title, message, options = {}) => addNotification({
      type: 'task',
      title,
      message,
      icon: 'ClipboardList',
      ...options
    }),

    client: (title, message, options = {}) => addNotification({
      type: 'client',
      title,
      message,
      icon: 'User',
      ...options
    }),

    system: (title, message, options = {}) => addNotification({
      type: 'system',
      title,
      message,
      icon: 'Settings',
      ...options
    }),

    ai: (title, message, options = {}) => addNotification({
      type: 'ai',
      title,
      message,
      icon: 'Sparkles',
      ...options
    })
  };

  const recordDeliveredAnnouncement = useCallback((announcementId) => {
    if (!announcementId) return;
    deliveredAnnouncementsRef.current.add(announcementId);
    localStorage.setItem('crm-announcements-delivered', JSON.stringify(Array.from(deliveredAnnouncementsRef.current)));
  }, []);

  useEffect(() => {
    if (!token) return;

    const severityToType = {
      info: 'info',
      success: 'success',
      warning: 'warning',
      danger: 'error'
    };

    let isCancelled = false;

    const loadAnnouncements = async () => {
      try {
        const response = await crmApi.get('/system/announcements');
        const payload = response.data;
        if (!payload?.success || !Array.isArray(payload.data)) {
          return;
        }

        payload.data.forEach((announcement) => {
          if (isCancelled) return;
          if (deliveredAnnouncementsRef.current.has(announcement.id)) {
            return;
          }
          // System announcements should appear under the "System" category
          const severityType = severityToType[announcement.severity] || 'info';
          addNotification({
            type: 'system', // Always use 'system' type for announcements
            title: announcement.title,
            message: announcement.message,
            icon: severityType === 'error' ? 'AlertCircle' : severityType === 'warning' ? 'AlertTriangle' : severityType === 'success' ? 'CheckCircle' : 'Info',
            source: 'announcement',
            sourceId: `announcement-${announcement.id}`,
            meta: {
              announcementId: announcement.id,
              sourceType: announcement.sourceType,
              sourceRef: announcement.sourceRef,
              publishedAt: announcement.publishedAt,
              expiresAt: announcement.expiresAt,
              severity: announcement.severity // Preserve severity in meta for styling if needed
            }
          });
          recordDeliveredAnnouncement(announcement.id);
        });
      } catch (error) {
        console.error('Failed to load system announcements:', error);
      }
    };

    loadAnnouncements();
    const interval = setInterval(loadAnnouncements, 5 * 60 * 1000);

    return () => {
      isCancelled = true;
      clearInterval(interval);
    };
  }, [token, addNotification, recordDeliveredAnnouncement]);

  const value = {
    notifications,
    unreadCount,
    isOpen,
    addNotification,
    markAsRead,
    markAllAsRead,
    removeNotification,
    clearAllNotifications,
    openNotificationCenter,
    closeNotificationCenter,
    toggleNotificationCenter,
    createNotification
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
